import { useEffect, useState } from "react"
import { ModalPopUp } from "../../common/element/ModalPopUp"
import { Header } from "../../common/header/Header"
import { useStore } from "../../../stores/RootStoreContext"
import { MenuManager } from "../MenuManager"
import { MenuAdmin } from "../../admin/MenuAdmin"
import { IModalMessage } from "../../../types/error/IModalMessage"
import { IMG } from "../../common/CONST"
import * as TrinityAPI from "../../../requests/TrinityAPI"
import { useNavigate, useParams } from "react-router-dom"
import { IOrderDto } from "../../../types/order/IOrderDto"
import { download, getFormattedDate, round2Digits, spacedNum } from "../../common/CommonHelper"
import { CommonFileInput } from "../../common/element/fields/CommonFileInput"
import { CommonInput } from "../../common/element/fields/CommonInput"
import { IErrorModel } from "../../../types/error/IErrorModel"
import { ModalConfirm } from "../../common/element/ModalConfirm"
import { IB64File } from "../../../types/IB64File"
import { CommonFileList } from "../../common/element/fields/CommonFileList"
import { IFullCompanyDto } from "../../../types/company/IFullCompanyDto"
import Button from "@/ui/components/Button"
import { ORDER_ROUTE } from "@/routes"
import { useUpdateEffect } from "react-use"

export const OrderManager = () => {
  const params = useParams()
  const { userStore } = useStore()
  const navigate = useNavigate()

  const [message, setMessage] = useState<IModalMessage | null>(null)
  const [confirmPayMessage, setConfirmPayMessage] = useState<IModalMessage>()
  const [errors, setErrors] = useState<IErrorModel>()
  const [order, setOrder] = useState<IOrderDto>()
  const [companies, setCompanies] = useState<IFullCompanyDto[]>([])
  const [company, setCompany] = useState<IFullCompanyDto>()
  const [calculatedUsd, setCalculatedUsd] = useState<number | undefined>(Number(order?.amountUsd))
  const [balance, setBalance] = useState<number>()
  const [rate, setRate] = useState(order?.xRate)
  const [sending, setSending] = useState(false)
  const [slipInvFiles, setSlipInvFiles] = useState<IB64File[]>([])
  const [refundAmount, setRefundAmount] = useState<string>()
  const [isDownloadingTxt, setIsDownloadingTxt] = useState(false)

  const isCurrencyOrder = !!order?.currencyReplenishmentId

  useEffect(() => {
    TrinityAPI.getOrder(Number(params.id), response => {
      if (response.status === 200) {
        setOrder({
          ...response.data,
          invoiceAmount: String(response.data.invoiceAmount / 100)
        })
      } else {
        setMessage({
          title: "Ошибка!",
          text: `Ошибка при загрузке ордера: ${response.data.message}`
        })
      }
    })

    TrinityAPI.getCompanyList(response => {
      if (response.status === 200) {
        setCompanies(response.data)
      } else {
        setMessage({ title: "Ошибка!", text: `Ошибка загрузки списка компаний: ${response.data}` })
      }
    })
  }, [])

  useEffect(() => {
    if (order && order.userId) {
      const files = new Array<IB64File>()
      if (order.agentInvoice) {
        files.push(order.agentInvoice)
      }
      if (order.slip) {
        files.push(order.slip)
      }
      setSlipInvFiles(files)

      TrinityAPI.getClientBalance(order.userId, response => {
        if (response.status === 200) {
          setBalance(response.data.amount / 100)
        }
      })
      setRate(order.xRate)
      setCalculatedUsd(Number(order.amountUsd))
    }
  }, [order?.id])

  useEffect(() => {
    if (rate || isCurrencyOrder) {
      const calculated = round2Digits(
        Number(order?.invoiceAmount) / Number(isCurrencyOrder ? 1 : rate?.toString().replaceAll(" ", ""))
      )
      setCalculatedUsd(calculated)
      setOrder({
        ...order,
        xRate: isCurrencyOrder ? "1" : rate?.toString().replaceAll(" ", ""),
        amountUsd: Math.round(calculated).toString()
      })
    } else {
      setCalculatedUsd(undefined)
      setOrder({ ...order, xRate: "", amountUsd: undefined })
    }
  }, [rate, isCurrencyOrder, order?.invoiceAmount])

  useUpdateEffect(() => {
    if (order?.invoiceAmount && rate) {
      const calculated = round2Digits(Number(order?.invoiceAmount) / Number(rate.toString().replaceAll(" ", "")))
      setCalculatedUsd(calculated)
      setOrder({
        ...order,
        amountUsd: Math.round(calculated).toString()
      })
    }
  }, [order?.invoiceAmount])

  useEffect(() => {
    if (order?.companyId && companies.length) {
      setCompany(companies.find(el => String(el.id) === String(order.companyId)))
    }
  }, [order?.companyId, companies])

  const changeCompany = (companyId: string) => {
    if (!order?.id) return
    TrinityAPI.changeOrderCompany(order?.id, companyId ? companyId : null, response => {
      if (response.status === 200) {
        setOrder({ ...order, companyId: companyId, bankId: undefined })
      } else {
        setMessage({ title: "Ошибка!", text: `Ошибка обновления компании: ${response.data}` })
      }
    })
  }

  const changeOrderBank = (bankId: string) => {
    if (!order?.id) return
    TrinityAPI.changeOrderBank(order?.id, bankId ? bankId : null, response => {
      if (response.status === 200) {
        setOrder({ ...order, bankId })
      } else {
        setMessage({ title: "Ошибка!", text: `Ошибка обновления банка: ${response.data}` })
      }
    })
  }

  const submitPayment = () => {
    if (sending) return
    if (!order?.agentInvoicePath && !order?.agentInvoice) {
      setMessage({ title: "Ошибка!", text: "Загрузите файл инвойса" })
      return
    }
    if (!order?.slipPath && !order?.slip) {
      setMessage({ title: "Ошибка!", text: "Загрузите файл slip" })
      return
    }
    if (!order?.xRate && !isCurrencyOrder) {
      setMessage({ title: "Ошибка!", text: "Заполните курс" })
      return
    }
    setSending(true)
    const payload = {
      ...order,
      xRate: isCurrencyOrder ? "1" : order?.xRate,
      invoiceAmount: Math.round(Number(order.invoiceAmount) * 100).toString(),
      amountUsd: String(Number(order.amountUsd) * 100)
    }
    TrinityAPI.payOrder(payload, response => {
      if (response.status === 200) {
        window.location.href = "/order"
      } else {
        setSending(false)
        setErrors(response.data)
      }
    })
  }

  const submitInquiry = () => {
    if (sending) return
    if (!order?.inquiryReport && !order?.inquiryReportPath) {
      setMessage({ title: "Ошибка!", text: "Загрузите файл расследования" })
      return
    }
    setSending(true)
    TrinityAPI.responseInquiry(order, response => {
      if (response.status === 200) {
        window.location.href = "/order"
      } else {
        setSending(false)
        setErrors(response.data)
      }
    })
  }

  const submitSwift = () => {
    if (sending) return
    if (!order?.swiftReport && !order?.swiftReportPath) {
      setMessage({ title: "Ошибка!", text: "Загрузите файл SWIFT" })
      return
    }
    setSending(true)
    TrinityAPI.responseSwift(order, response => {
      if (response.status === 200) {
        window.location.href = "/order"
      } else {
        setSending(false)
        setErrors(response.data)
      }
    })
  }

  const submitInqSwift = () => {
    if (sending) return
    if (!order?.inquiryReport && !order?.inquiryReportPath) {
      setMessage({ title: "Ошибка!", text: "Загрузите файл расследования" })
      return
    }
    if (!order?.swiftReport && !order?.swiftReportPath) {
      setMessage({ title: "Ошибка!", text: "Загрузите файл SWIFT" })
      return
    }
    setSending(true)
    TrinityAPI.responseInquirySwift(order, response => {
      if (response.status === 200) {
        window.location.href = "/order"
      } else {
        setSending(false)
        setErrors(response.data)
      }
    })
  }

  const refund = () => {
    if (!refundAmount || !order?.id) {
      setMessage({ title: "Ошибка!", text: "Введите сумму для возврата" })
      return
    }
    setSending(true)
    TrinityAPI.refundOrder(order.id, (Number(refundAmount.replaceAll(" ", "")) * 100).toString(), response => {
      if (response.status === 200) {
        window.location.href = "/order"
      } else {
        setErrors(response.data)
        setSending(false)
      }
    })
  }

  const rollbackStatus = () => {
    if (!order?.id) return
    setSending(true)
    TrinityAPI.rollbackOrder(order.id, response => {
      if (response.status === 200) {
        window.location.reload()
      } else {
        setErrors(response.data)
        setSending(false)
      }
    })
  }

  const confirmSubmit = (v: boolean) => {
    if (v) submitPayment()
  }

  const confirmRefund = (v: boolean) => {
    if (v) refund()
  }

  const downloadTxt = () => {
    if (!order?.id) return
    setIsDownloadingTxt(true)
    TrinityAPI.downloadOrderTxt(order.id, response => {
      if (response.status === 200) {
        const contentDisposition: string = response.headers?.["content-disposition"] || ""
        const match = contentDisposition.match(/filename="?([^";\n]+)"?/)
        const dateStr = order.invoiceDate ? order.invoiceDate.split("T")[0].split("-").reverse().join(".") : ""
        const filename = match ? match[1] : `order_${order.id}${dateStr ? `_dated_${dateStr}` : ""}.txt`
        const blob = new Blob([response.data], { type: "text/plain" })
        const blobUrl = URL.createObjectURL(blob)
        const a = document.createElement("a")
        a.href = blobUrl
        a.download = filename
        a.click()
        URL.revokeObjectURL(blobUrl)
      } else {
        setMessage({ title: "Ошибка!", text: "Ошибка при выгрузке файла" })
      }
      setIsDownloadingTxt(false)
    }, () => {
      setIsDownloadingTxt(false)
      setMessage({ title: "Ошибка!", text: "Ошибка при выгрузке файла" })
    })
  }

  useUpdateEffect(() => {
    const timerId = setTimeout(() => {
      if (!order?.id) return

      TrinityAPI.updateOrderComment(order.id, order.comment || "")
    }, 400)

    return () => clearTimeout(timerId)
  }, [order?.comment])

  return (
    <>
      <ModalConfirm message={confirmPayMessage} setMessage={setConfirmPayMessage} />
      <ModalPopUp message={message} close={() => setMessage(null)} error={errors} />
      {userStore.isManager ? <MenuManager active={"order"} /> : <MenuAdmin active={"order"} />}
      <main className="main-container" id="scrollable">
        <Header />
        <div className="container" style={{ maxWidth: "1600px" }}>
          <h3 className="ui-heading">Ордер №{order?.id}</h3>
          <div className="container-pop-up">
            <ul className="form-left">
              <li className="form-block-popup">
                <h4 className="heading-chapter">Инвойс</h4>
                <div className="flex-row-10 flex-wrap-mobile">
                  <fieldset>
                    <legend>Дата</legend>
                    <p className="text-form">{getFormattedDate(order?.invoiceDate || "")}</p>
                  </fieldset>
                  <fieldset>
                    <legend>Номер</legend>
                    <p className="text-form">№{order?.invoiceNumber}</p>
                  </fieldset>
                  <fieldset className="fieldset-fit">
                    <legend>Файл инвойса</legend>
                    <button
                      className="btn download-btn"
                      onClick={() => download(order?.id?.toString(), "ORDER_CLIENT_INVOICE")}
                    >
                      <img src={IMG.download} alt="" />
                      Скачать
                    </button>
                  </fieldset>
                </div>
              </li>
              <li className="form-block-popup">
                <h4 className="heading-chapter">Деньги</h4>
                <div className="flex-row-10">
                  <CommonInput
                    typeName="number"
                    label="Сумма"
                    placeholder="Введите сумму"
                    name="invoiceAmount"
                    value={order?.invoiceAmount}
                    errorModel={errors}
                    disabled={order?.status !== "NEW"}
                    _callback={v => setOrder({ ...order, invoiceAmount: v?.toString().replaceAll(" ", "") || "" })}
                  />
                  <fieldset className="fieldset-200">
                    <legend>Валюта</legend>
                    <p className="text-form">{order?.invoiceCurrency}</p>
                  </fieldset>
                </div>
              </li>
              <li className="form-block-popup">
                <h4 className="heading-chapter">Информация о получателе</h4>
                <fieldset>
                  <legend>Контактное лицо</legend>
                  <p className="text-form">{order?.contractorContactName}</p>
                </fieldset>
                {/*<fieldset>*/}
                {/*  <legend>Должность</legend>*/}
                {/*  <p className="text-form">{order?.contractorContactJobTitle}</p>*/}
                {/*</fieldset>*/}
                <div className="flex-row-10">
                  {/*<fieldset>*/}
                  {/*  <legend>email</legend>*/}
                  {/*  <p className="text-form">{order?.contractorContactEmail}</p>*/}
                  {/*</fieldset>*/}
                  <fieldset>
                    <legend>Телефон</legend>
                    <p className="text-form">{order?.contractorContactPhone}</p>
                  </fieldset>
                </div>
              </li>
              <li className="form-block-popup">
                <h4 className="heading-chapter">Получатель валютных средств</h4>
                <fieldset>
                  <legend>Полное название</legend>
                  <p className="text-form">{order?.contractorName}</p>
                </fieldset>
                <fieldset>
                  <legend>Страна</legend>
                  <p className="text-form">{order?.contractorCountry?.name}</p>
                </fieldset>
                <fieldset>
                  <legend>Адрес</legend>
                  <p className="text-form">{order?.contractorAddress}</p>
                </fieldset>
              </li>
              <li className="form-block-popup">
                <h4 className="heading-chapter">Счет</h4>
                <fieldset>
                  <legend>Банк</legend>
                  <p className="text-form">{order?.contractorBankName}</p>
                </fieldset>
                <div className="flex-row-10">
                  <fieldset>
                    <legend>IBAN</legend>
                    <p className="text-form">{order?.contractorAccountNumber}</p>
                  </fieldset>
                  <fieldset>
                    <legend>SWIFT</legend>
                    <p className="text-form">{order?.contractorSwift}</p>
                  </fieldset>
                </div>
              </li>
            </ul>
            <ul className="right-form">
              <li className="form-block-popup">
                <h4 className="heading-chapter">Подтверждающие документы</h4>
                <div className="flex-row-10">
                  {order?.status === "NEW" ? (
                    <div className="flex-column-24">
                      <fieldset>
                        <legend>Загрузить новые файлы</legend>
                        <div className="flex-row-10">
                          <CommonFileInput
                            label={""}
                            placeholder={order?.slip ? "Заменить slip" : "Загрузить slip"}
                            name={"slip"}
                            errorModel={undefined}
                            _callback={f => setOrder({ ...order, slip: f })}
                            justButton={true}
                          />
                          <CommonFileInput
                            label={""}
                            placeholder={
                              order?.agentInvoice ? "Заменить агентский инвойс" : "Загрузить агентский инвойс"
                            }
                            name={"agentInvoice"}
                            errorModel={undefined}
                            _callback={f => setOrder({ ...order, agentInvoice: f })}
                            justButton={true}
                          />
                        </div>
                      </fieldset>
                      {order?.agentInvoicePath && order?.slipPath && (
                        <fieldset>
                          <legend>Скачать существующие файлы</legend>
                          <div className="flex-row-10">
                            <button
                              className="btn download-btn"
                              onClick={() => download(order?.id?.toString(), "ORDER_SLIP")}
                            >
                              <img src={IMG.download} alt="" />
                              slip
                            </button>
                            <button
                              className="btn download-btn"
                              onClick={() => download(order?.id?.toString(), "ORDER_AGENT_INVOICE")}
                            >
                              <img src={IMG.download} alt="" />
                              Агентский инвойс
                            </button>
                          </div>
                        </fieldset>
                      )}
                    </div>
                  ) : (
                    <fieldset>
                      <div className="flex-row-10">
                        <button
                          className="btn download-btn"
                          onClick={() => download(order?.id?.toString(), "ORDER_SLIP")}
                        >
                          <img src={IMG.download} alt="" />
                          slip
                        </button>
                        <button
                          className="btn download-btn"
                          onClick={() => download(order?.id?.toString(), "ORDER_AGENT_INVOICE")}
                        >
                          <img src={IMG.download} alt="" />
                          Агентский инвойс
                        </button>
                      </div>
                    </fieldset>
                  )}
                </div>
                <CommonFileList files={slipInvFiles} />
              </li>
              {!isCurrencyOrder && (
                <li className="form-block-popup">
                  <h4 className="heading-chapter">Фиксация курса</h4>
                  <CommonInput
                    typeName={"number"}
                    label={`Курс USD/${order?.invoiceCurrency} на момент оплаты`}
                    placeholder={"Введите курс"}
                    name={"xRate"}
                    value={rate}
                    errorModel={errors}
                    disabled={order?.status !== "NEW"}
                    _callback={v => setRate(v)}
                  />
                </li>
              )}

              <div className="form-block-popup">
                <h4 className="heading-chapter">
                  {order?.status === "NEW" || order?.status === "CANCELLED"
                    ? "Предварительный расчет"
                    : "Окончательный расчет"}
                </h4>
                <div className="flex-row-10">
                  <fieldset>
                    <legend className="legend-no-padding">Сумма в {order?.invoiceCurrency}</legend>
                    <p className="text-form-final">{spacedNum(Number(order?.invoiceAmount))}</p>
                  </fieldset>
                  <fieldset>
                    <legend className="legend-no-padding">Сумма в USD</legend>
                    <p className="text-form-final">{calculatedUsd ? spacedNum(calculatedUsd) : "-"}</p>
                  </fieldset>
                </div>
                {order?.status === "NEW" ? (
                  <div className="flex-row-10">
                    <fieldset>
                      <legend className="legend-no-padding">Баланс клиента до</legend>
                      <p className="text-form-final">{spacedNum(balance || "") || "-"}</p>
                    </fieldset>
                    <fieldset>
                      <legend className="legend-no-padding">Баланс клиента после</legend>
                      <p className="text-form-final">
                        {balance && calculatedUsd ? spacedNum(round2Digits(balance - calculatedUsd)) : "-"}
                      </p>
                    </fieldset>
                  </div>
                ) : (
                  <div className="flex-row-10">
                    <fieldset>
                      <legend className="legend-no-padding">Баланс клиента</legend>
                      <p className="text-form-final">{spacedNum(balance || "") || "-"}</p>
                    </fieldset>
                    {order?.refundAmount && (
                      <fieldset>
                        <legend className="legend-no-padding">Сумма возврата</legend>
                        <p className="text-form-final">{spacedNum(round2Digits(Number(order.refundAmount) / 100))}</p>
                      </fieldset>
                    )}
                  </div>
                )}
              </div>

              <div className="form-block-popup">
                <h4 className="heading-chapter">Компания плательщик</h4>
                <div className="flex-column-16">
                  <fieldset>
                    <legend>Выберите компанию, через которую будет произведена оплата</legend>
                    <select
                      value={order?.companyId}
                      onChange={e => changeCompany(e.target.value)}
                      className="text-form"
                    >
                      <option value="">Выберите компанию</option>
                      {companies.map((c, index) => (
                        <option value={c.id} key={index}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </fieldset>

                  <fieldset style={!company ? { pointerEvents: "none", opacity: "0.5" } : {}}>
                    <legend>Выберите счет</legend>
                    <select value={order?.bankId} onChange={e => changeOrderBank(e.target.value)} className="text-form">
                      <option value="">Выберите счет</option>
                      {(company?.banks?.length || 0) > 0
                        ? company?.banks?.map((c, index) => (
                            <option value={String(c.id)} key={index}>
                              #{c.id} {c.swift} - {c.accountNumber} - {c.currency}
                            </option>
                          ))
                        : null}
                    </select>
                  </fieldset>
                </div>
              </div>

              <div className="form-block-popup">
                <h4 className="heading-chapter">Комментарий</h4>
                <div className="flex-column-5">
                  <input
                    type="text"
                    className="input-form"
                    placeholder="Введите комментарий"
                    onChange={e => setOrder({ ...order, comment: e.target.value })}
                    value={order?.comment}
                  />
                </div>
              </div>

              {order?.inquiryReportPath ? (
                <li className="form-block-popup">
                  <h4 className="heading-chapter">Расследование</h4>
                  <p className="text-block">Загруженный файл расследования</p>
                  <button className="btn download-btn" onClick={() => download(order?.id?.toString(), "ORDER_INQUIRY")}>
                    <img src={IMG.download} alt="" />
                    Скачать
                  </button>
                </li>
              ) : (
                <></>
              )}

              {order?.status === "INQUIRY" || order?.status === "INQ_SWIFT" ? (
                <li className="form-block-popup">
                  <h4 className="heading-chapter">Расследование</h4>
                  <p className="text-block">Клиент запросил расследование</p>
                  <div className="flex-row-10">
                    <CommonFileInput
                      label={"Загрузка файла"}
                      placeholder={order.inquiryReport ? "Заменить файл расследования" : "Загрузить файл расследования"}
                      name={""}
                      errorModel={undefined}
                      _callback={f => setOrder({ ...order, inquiryReport: f })}
                    />
                  </div>
                  <CommonFileList file={order.inquiryReport} />
                </li>
              ) : (
                <></>
              )}

              {order?.swiftReportPath ? (
                <li className="form-block-popup">
                  <h4 className="heading-chapter">SWIFT</h4>
                  <p className="text-block">Загруженный файл SWIFT</p>
                  <button className="btn download-btn" onClick={() => download(order?.id?.toString(), "ORDER_SWIFT")}>
                    <img src={IMG.download} alt="" />
                    Скачать
                  </button>
                </li>
              ) : (
                <></>
              )}

              {order?.status === "SWIFT" || order?.status === "INQ_SWIFT" ? (
                <li className="form-block-popup">
                  <h4 className="heading-chapter">SWIFT</h4>
                  <p className="text-block">Клиент запросил SWIFT</p>
                  <div className="flex-row-10">
                    <CommonFileInput
                      label={"Загрузка файла"}
                      placeholder={order.swiftReport ? "Заменить файл SWIFT" : "Загрузить файл SWIFT"}
                      name={""}
                      errorModel={undefined}
                      _callback={f => setOrder({ ...order, swiftReport: f })}
                    />
                  </div>
                  <CommonFileList file={order.swiftReport} />
                </li>
              ) : (
                <></>
              )}

              {order?.status !== "NEW" &&
              order?.status !== "CANCELLED" &&
              order?.status !== "REFUNDED" &&
              userStore.isAdmin ? (
                <li className="form-block-popup">
                  <h4 className="heading-chapter">Возврат</h4>
                  <p className="text-block">Произвести возврат средств</p>
                  <div className="flex-row-10">
                    <CommonInput
                      typeName={"number"}
                      label={"Сумма"}
                      placeholder={`Сумма в ${order?.invoiceCurrency || "USD"}`}
                      name={"refundAmount"}
                      value={refundAmount}
                      errorModel={undefined}
                      _callback={v => setRefundAmount(v)}
                    />
                  </div>
                  <div className="flex-row-10">
                    <button
                      className="btn"
                      onClick={() =>
                        setConfirmPayMessage({
                          title: `Вернуть сумму ${refundAmount || 0} на счет клиенту?`,
                          text: `После подтверждения сумма в размере ${
                            refundAmount || 0
                          } ${order?.invoiceCurrency || "USD"} будет возвращена на счет клиенту, а ордер перейдет в финальный статус`,
                          callbackValue: true,
                          _callback: confirmRefund
                        })
                      }
                    >
                      {sending ? "Подождите..." : "Вернуть"}
                    </button>
                  </div>
                </li>
              ) : (
                <></>
              )}
            </ul>
            <div className="flex-row-10 flex-wrap">
              {order?.status === "NEW" && (
                <Button
                  onClick={() =>
                    setConfirmPayMessage({
                      title: "Подтверждаете оплату?",
                      text: `После подтверждения и загрузки документов со счета пользователя будет списано ${spacedNum(
                        Number(order?.invoiceAmount) || 0
                      )} ${order?.invoiceCurrency || "USD"}`,
                      callbackValue: true,
                      _callback: confirmSubmit
                    })
                  }
                  loading={sending}
                >
                  Отправить
                </Button>
              )}
              {userStore.isAdmin &&
                order?.status !== "NEW" &&
                order?.status !== "REFUNDED" &&
                order?.status !== "CANCELLED" && (
                  <Button loading={sending} onClick={() => rollbackStatus()}>
                    Откатить
                  </Button>
                )}
              {order?.status === "INQUIRY" && (
                <Button className="btn" onClick={submitInquiry}>
                  Отправить
                </Button>
              )}
              {order?.status === "SWIFT" && (
                <Button className="btn" onClick={submitSwift}>
                  Отправить
                </Button>
              )}
              {order?.status === "INQ_SWIFT" && (
                <Button className="btn" onClick={submitInqSwift}>
                  Отправить
                </Button>
              )}
              <Button onClick={downloadTxt} loading={isDownloadingTxt}>
                Выгрузить
              </Button>
              <Button variant="grey" onClick={() => navigate(ORDER_ROUTE)}>
                Назад
              </Button>
            </div>
          </div>
        </div>
      </main>
    </>
  )
}
